"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type { Locale } from "@/lib/i18n";

const easeOut = [0.22, 1, 0.36, 1] as const;

type IconKey =
  | "chat"
  | "health"
  | "invoice"
  | "pill"
  | "consent"
  | "calendar"
  | "card"
  | "code";

type Category = {
  key: string;
  icon: IconKey;
  title: { tr: string; en: string };
  desc: { tr: string; en: string };
  items: string[];
};

const CATEGORIES: Category[] = [
  {
    key: "iletisim",
    icon: "chat",
    title: { tr: "İletişim Araçları", en: "Communication Tools" },
    desc: {
      tr: "WhatsApp, SMS, e-posta ve çağrı merkeziyle hasta iletişimi",
      en: "Patient messaging via WhatsApp, SMS, email and call center",
    },
    items: ["WhatsApp", "Messenger", "Instagram DM", "SMS", "E-posta", "Çağrı Merkezi"],
  },
  {
    key: "saglik-net",
    icon: "health",
    title: { tr: "Sağlık.NET & MHRS", en: "Sağlık.NET & MHRS" },
    desc: {
      tr: "e-Nabız, e-Reçete, e-Rapor ve MHRS randevu aktarımı",
      en: "e-Nabız, e-prescription, e-report and MHRS transfer",
    },
    items: ["e-Nabız", "e-Reçete", "e-Rapor", "MHRS"],
  },
  {
    key: "e-fatura",
    icon: "invoice",
    title: { tr: "e-Fatura & e-Arşiv", en: "e-Invoice & e-Archive" },
    desc: {
      tr: "Elektronik fatura, e-arşiv ve muhasebe entegrasyonu",
      en: "Electronic invoicing, e-archive and accounting",
    },
    items: ["e-Fatura", "e-Arşiv", "e-İrsaliye", "Muhasebe"],
  },
  {
    key: "its",
    icon: "pill",
    title: { tr: "İlaç Takip Sistemi (İTS)", en: "Drug Tracking (İTS)" },
    desc: {
      tr: "Mal alım ve sarf bildirimleri, ürün takibi",
      en: "Purchase and consumption notifications, product tracking",
    },
    items: ["Mal Alım Bildirimi", "Sarf Bildirimi", "Ürün Takibi"],
  },
  {
    key: "onam",
    icon: "consent",
    title: { tr: "Dijital Onam Formları", en: "Digital Consent Forms" },
    desc: {
      tr: "e-imza ile hasta onam ve onay yönetimi",
      en: "e-signature patient consent and approval",
    },
    items: ["e-İmza", "Hasta Onam Formu", "KVKK Rızası"],
  },
  {
    key: "randevu",
    icon: "calendar",
    title: { tr: "Randevu Sistemleri", en: "Appointment Systems" },
    desc: {
      tr: "Online randevu ve Google Takvim senkronizasyonu",
      en: "Online booking and Google Calendar sync",
    },
    items: ["Online Randevu", "Google Takvim", "Otomatik Hatırlatma"],
  },
  {
    key: "odeme",
    icon: "card",
    title: { tr: "Ödeme & Sanal POS", en: "Payments & Virtual POS" },
    desc: {
      tr: "Sanal POS, taksitli tahsilat ve online ödeme",
      en: "Virtual POS, installments and online payments",
    },
    items: ["Sanal POS", "Taksitli Tahsilat", "Online Ödeme"],
  },
  {
    key: "gelistirici",
    icon: "code",
    title: { tr: "Geliştirici Araçları", en: "Developer Tools" },
    desc: {
      tr: "REST API, webhook ve özel entegrasyonlar",
      en: "REST API, webhooks and custom integrations",
    },
    items: ["REST API", "Webhook", "Özel Entegrasyon"],
  },
];

const ICON_PATHS: Record<IconKey, string> = {
  chat: "M7.5 9h9M7.5 12.5h6M21 11.5a7.5 7.5 0 0 1-11 6.6L4 20l1.4-4.2A7.5 7.5 0 1 1 21 11.5Z",
  health: "M12 20.5C7 17 3.5 13.8 3.5 10.2A4.2 4.2 0 0 1 12 7.4a4.2 4.2 0 0 1 8.5 2.8c0 3.6-3.5 6.8-8.5 10.3Z",
  invoice:
    "M6 3.5h7L18.5 9v10.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1ZM13 3.5V9h5.5M8.5 13h7M8.5 16.5h5",
  pill: "M4 13 13 4a4.5 4.5 0 0 1 6.4 6.4L10.4 19.4A4.5 4.5 0 0 1 4 13Zm4.5-4.5 6.5 6.5",
  consent:
    "M9 4.5h6M9 4.5A1.5 1.5 0 0 1 10.5 3h3A1.5 1.5 0 0 1 15 4.5V5a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-.5ZM15 4.5h3a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5.5a1 1 0 0 1 1-1h3m0 8.5 2 2 4-4",
  calendar: "M4.5 6.5A1.5 1.5 0 0 1 6 5h12a1.5 1.5 0 0 1 1.5 1.5V19a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 19V6.5ZM4.5 9.5h15M8 3.5v3M16 3.5v3",
  card: "M3.5 7.5A1.5 1.5 0 0 1 5 6h14a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 19 17.5H5A1.5 1.5 0 0 1 3.5 16.5v-9ZM3.5 10h17M7 14h3",
  code: "m9 8-4 4 4 4M15 8l4 4-4 4",
};

function Icon({ name }: { name: IconKey }) {
  return (
    <svg
      className="h-5 w-5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

function demoHref(locale: Locale) {
  return locale === "en" ? "/en/contact#demo" : "/iletisim#demo";
}

/** Masaüstü mega-menü — tüm kategoriler 3 sütunlu grid (scroll yok). */
export function NavIntegrations({ locale }: { locale: Locale }) {
  const prefersReducedMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);

  const label = locale === "en" ? "Integrations" : "Entegrasyonlar";

  const openNow = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 130);
  };

  return (
    <div
      className="relative shrink-0"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onFocus={openNow}
        aria-expanded={open}
        aria-haspopup="true"
        className="relative flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-3 text-[15px] font-light tracking-normal text-[#172554] transition-[letter-spacing,font-weight] duration-300 ease-out hover:font-medium hover:tracking-wide motion-reduce:hover:font-light motion-reduce:hover:tracking-normal dark:text-white cloud:text-[#4338ca] cloud:hover:text-[#6157e1] lg:px-3 2xl:px-2.5 2xl:text-base"
      >
        {label}
        <svg
          className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            role="menu"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: easeOut }}
            className="absolute left-1/2 top-full z-50 mt-3 w-[min(94vw,880px)] -translate-x-1/2 overflow-hidden rounded-2xl border border-border bg-background p-3 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)]"
          >
            <div className="grid grid-cols-3 gap-1">
              {CATEGORIES.map((cat) => (
                <Link
                  key={cat.key}
                  href={demoHref(locale)}
                  onClick={() => setOpen(false)}
                  className="flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-card"
                >
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-foreground">
                    <Icon name={cat.icon} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium leading-tight text-foreground">
                      {cat.title[locale]}
                    </span>
                    <span className="mt-1 block truncate text-xs text-muted">
                      {cat.items.slice(0, 3).join(" · ")}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-1 flex items-center justify-between border-t border-border px-3 pt-3">
              <p className="text-xs text-muted">
                {locale === "en"
                  ? "Ready-made & custom integrations"
                  : "Hazır ve özel entegrasyonlar"}
              </p>
              <Link
                href={demoHref(locale)}
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1 text-sm font-medium text-[#F97316] underline-offset-4 hover:underline"
              >
                {locale === "en" ? "Request a demo" : "Demo talep et"} →
              </Link>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/** Mobil menü içinde açılır "Entegrasyonlar" bölümü */
export function NavIntegrationsMobile({
  locale,
  onNavigate,
}: {
  locale: Locale;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const label = locale === "en" ? "Integrations" : "Entegrasyonlar";

  return (
    <div className="border-t border-border/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-xl px-3 py-4 text-lg font-light text-[#172554]/85 dark:text-white/85 cloud:text-[#4338ca]"
      >
        {label}
        <svg
          className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <ul className="space-y-1 pb-2">
          {CATEGORIES.map((cat) => (
            <li key={cat.key} className="flex items-start gap-3 rounded-xl px-3 py-2.5">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-foreground">
                <Icon name={cat.icon} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">
                  {cat.title[locale]}
                </span>
                <span className="mt-0.5 block text-xs text-muted">{cat.items.join(" · ")}</span>
              </span>
            </li>
          ))}
          <li className="px-3 pt-1">
            <Link
              href={demoHref(locale)}
              onClick={onNavigate}
              className="text-sm font-medium text-foreground underline underline-offset-4"
            >
              {locale === "en" ? "Request a demo" : "Demo talep et"} →
            </Link>
          </li>
        </ul>
      ) : null}
    </div>
  );
}
